import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { AuditOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { toast } from "react-toastify";
import {
  createInventorySettlement,
  getAllStock,
  getInventorySettlements,
} from "../../api/inventoryApi";
import { searchOrderItems } from "../../api/orderApi";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const flattenStockOptions = (groups = []) =>
  groups.flatMap((group) =>
    (group.batches || []).map((batch) => ({
      id: batch.id,
      label: `${group.material?.name || "Material"}${group.reel_width ? ` • RS ${group.reel_width}"` : ""}${batch.batch_no ? ` • Batch ${batch.batch_no}` : ""}`,
      system_quantity: batch.quantity,
      available_qty: batch.available_qty,
      unit: group.material?.unit,
      material_code: group.material?.code,
      reel_width: batch.reel_width,
    }))
  );

const InventorySettlement = () => {
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  const accessToken = localStorage.getItem("access-token");

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [settlements, setSettlements] = useState([]);
  const [stockGroups, setStockGroups] = useState([]);
  const [orderOptions, setOrderOptions] = useState([]);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);
  const watchedStockId = Form.useWatch("stock_id", form);
  const watchedActualQty = Form.useWatch("actual_quantity", form);

  const stockOptions = useMemo(() => flattenStockOptions(stockGroups), [stockGroups]);
  const selectedStock = stockOptions.find((item) => item.id === watchedStockId);
  const variance =
    selectedStock && watchedActualQty != null
      ? Number((Number(watchedActualQty) - Number(selectedStock.system_quantity)).toFixed(3))
      : null;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [settlementResp, stockResp] = await Promise.all([
        getInventorySettlements({
          from_date: dateRange[0].format("YYYY-MM-DD"),
          to_date: dateRange[1].format("YYYY-MM-DD"),
        }),
        getAllStock(),
      ]);

      setSettlements(settlementResp?.data || []);
      setStockGroups(stockResp || []);
    } catch (error) {
      toast.error("Failed to load inventory settlements");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!isLogged) {
    return <Navigate to="/auth/login" replace />;
  }

  checkTokenExp(accessToken);

  const handleSearchOrders = async (search) => {
    if (!search || search.trim().length < 2) {
      setOrderOptions([]);
      return;
    }

    try {
      const response = await searchOrderItems({ search, limit: 10 });
      setOrderOptions(response?.data || []);
    } catch (error) {
      setOrderOptions([]);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const selectedOrderItem = orderOptions.find((item) => item.id === values.mapped_order_item_id);

      await createInventorySettlement({
        stock_id: values.stock_id,
        actual_quantity: values.actual_quantity,
        reason: values.reason,
        notes: values.notes,
        mapped_order_id: selectedOrderItem?.order?.id,
        mapped_order_item_id: selectedOrderItem?.id,
      });

      toast.success("Settlement recorded");
      setOpen(false);
      form.resetFields();
      setOrderOptions([]);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to record settlement");
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (value) => moment(value).format("DD MMM YYYY, hh:mm A"),
    },
    {
      title: "Inventory",
      key: "inventory",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.material_name || row.material?.name || "-"}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.material_code || row.material?.code || "-"}
            {row.reel_width ? ` • RS ${row.reel_width}"` : ""}
            {row.batch_no ? ` • Batch ${row.batch_no}` : ""}
          </Text>
        </Space>
      ),
    },
    {
      title: "System",
      dataIndex: "system_quantity",
      key: "system_quantity",
      width: 110,
      render: (value, row) => `${value} ${row.unit}`,
    },
    {
      title: "Actual",
      dataIndex: "actual_quantity",
      key: "actual_quantity",
      width: 110,
      render: (value, row) => `${value} ${row.unit}`,
    },
    {
      title: "Variance",
      dataIndex: "variance_quantity",
      key: "variance_quantity",
      width: 110,
      render: (value) => (
        <Text type={value < 0 ? "danger" : value > 0 ? "success" : undefined}>
          {value}
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "settlement_type",
      key: "settlement_type",
      width: 110,
      render: (value) => <Tag color={value === "SHORTAGE" ? "red" : value === "EXCESS" ? "green" : "blue"}>{value}</Tag>,
    },
    {
      title: "Mapped Order",
      key: "mapped_order",
      render: (_, row) => row.mapped_order_number || "-",
    },
    {
      title: "Marked By",
      dataIndex: "created_by_name",
      key: "created_by_name",
      width: 150,
      render: (value) => value || "System",
    },
  ];

  return (
    <>
      <PageTitle title="Back" subtitle="Inventory Settlement" />

      <Card
        extra={
          <Space>
            <RangePicker
              value={dateRange}
              onChange={(dates) => dates && setDateRange(dates)}
              allowClear={false}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchData}>
              Refresh
            </Button>
            <Button type="primary" icon={<AuditOutlined />} onClick={() => setOpen(true)}>
              New Settlement
            </Button>
          </Space>
        }
      >
        <Table
          loading={loading}
          columns={columns}
          dataSource={settlements}
          rowKey="id"
          scroll={{ x: 980 }}
        />
      </Card>

      <Modal
        title="Record Inventory Settlement"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={submitting}
        okText="Save"
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Inventory Batch / Reel"
            name="stock_id"
            rules={[{ required: true, message: "Select inventory" }]}
          >
            <Select showSearch placeholder="Select stock entry" optionFilterProp="children">
              {stockOptions.map((option) => (
                <Option key={option.id} value={option.id}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedStock && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" size={2}>
                <Text type="secondary">
                  System Quantity: {selectedStock.system_quantity} {selectedStock.unit}
                </Text>
                <Text type="secondary">
                  Available Quantity: {selectedStock.available_qty} {selectedStock.unit}
                </Text>
              </Space>
            </Card>
          )}

          <Form.Item
            label="Audited / Actual Quantity"
            name="actual_quantity"
            rules={[{ required: true, message: "Enter actual quantity" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>

          {variance != null && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Text strong type={variance < 0 ? "danger" : variance > 0 ? "success" : undefined}>
                Variance: {variance} {selectedStock?.unit}
              </Text>
            </Card>
          )}

          <Form.Item label="Map to Order Item (Optional)" name="mapped_order_item_id">
            <Select
              showSearch
              allowClear
              placeholder="Search order / item"
              filterOption={false}
              onSearch={handleSearchOrders}
              notFoundContent="Type at least 2 characters"
            >
              {orderOptions.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.order?.order_number} - {item.item_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Reason" name="reason">
            <Input placeholder="Audit shortage / excess reason" />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default InventorySettlement;
